import { useEffect, useMemo } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import { publishMetrics } from '@zooplus/delivery-services-metrics-ui-agent';

import { getAppConfig } from '@config/get-app-config';
import { logError } from '@services/sentry-service';
import { CHECKOUT_STATE_PROJECT_ID, TRACKING_DATA_PAGE, CUSTOMER_TYPE } from '@utilities/constants';

const useDeliveryServicesMetrics = () => {
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { cart, customer } = checkoutState ?? {};
  const { site, deviceType } = getAppConfig();

  const siteConfig = {
    id: site?.id,
    domain: site?.domain,
    locale: site?.locale,
    currencyCode: site?.alternativeCurrency,
  };

  const cartConfig = useMemo(
    () => ({
      sid: cart?.sid ?? '',
      cartId: cart?.cartId?.toString() ?? '',
    }),
    [cart?.sid, cart?.cartId],
  );

  const pageConfig = useMemo(
    () => ({
      name: TRACKING_DATA_PAGE.CHECKOUT_CART,
      pageLocation: window.location.href,
    }),
    [],
  );

  const customerConfig = useMemo(
    () => ({
      id: customer?.customerId?.toString() ?? '',
      isGuest: customer?.customerType === CUSTOMER_TYPE.GUEST_CUSTOMER,
    }),
    [customer?.customerId, customer?.customerType],
  );

  const isMetricsEligibleForSending = useMemo(
    () =>
      Boolean(
        cart?.cartId &&
          siteConfig.id &&
          siteConfig.currencyCode &&
          siteConfig.domain &&
          siteConfig.locale,
      ),
    [cart?.cartId, siteConfig.id, siteConfig.currencyCode, siteConfig.domain, siteConfig.locale],
  );

  useEffect(() => {
    const publishDeliveryMetrics = async () => {
      if (!isMetricsEligibleForSending) {
        return;
      }

      try {
        await publishMetrics(
          cartConfig.sid,
          cartConfig.cartId,
          siteConfig,
          pageConfig,
          deviceType,
          customerConfig,
        );
      } catch (error) {
        logError(new Error(`Failed to publish delivery services metrics: ${error}`));
      }
    };

    const timeoutId = setTimeout(() => {
      void publishDeliveryMetrics();
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, [cartConfig, pageConfig, customerConfig, deviceType, isMetricsEligibleForSending]);

  return null;
};

export default useDeliveryServicesMetrics;
